import React, { useState, useEffect, useContext } from 'react';
import { AlertCircle } from 'lucide-react';
import AppContext from '../../contexts/AppContext';
import * as firestoreService from '../../services/firestoreService';
import { logoutUser } from '../../services/authService';

const RestrictedScreen = () => {
  const { currentUser, showAlert, effectiveTheme } = useContext(AppContext);
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [myDisputes, setMyDisputes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingDisputes, setLoadingDisputes] = useState(true);

  const isBanned = currentUser.status === 'banned';
  const penaltyReason = isBanned
    ? (currentUser.banReason || 'Violation of community guidelines')
    : (currentUser.suspensionReason || 'Violation of community guidelines');

  useEffect(() => {
    firestoreService.getUserDisputes(currentUser.id).then(d => {
      setMyDisputes(d);
      setLoadingDisputes(false);
    }).catch(() => setLoadingDisputes(false));
  }, [currentUser.id]);

  const hasPendingDispute = myDisputes.some(d => d.status === 'pending');

  const handleSubmitDispute = async () => {
    if (!disputeReason.trim() || submitting) return;
    setSubmitting(true);
    try {
      const dispute = await firestoreService.createDispute({
        userId: currentUser.id,
        userEmail: currentUser.email,
        userName: currentUser.name,
        penaltyType: isBanned ? 'banned' : 'suspended',
        reason: disputeReason.trim(),
        penaltyReason,
      });
      setMyDisputes([{ ...dispute, timestamp: new Date().toISOString() }, ...myDisputes]);
      setDisputeReason('');
      setShowDisputeForm(false);
    } catch (err) {
      console.error('Error submitting dispute:', err);
      showAlert('Failed to submit dispute. Please try again.', 'Error');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img
            src={effectiveTheme === 'dark' ? '/logos/logo-loft-dark-theme-582x243.png' : '/logos/logo-loft-light-theme-582x243.png'}
            alt="Loft"
            className="mx-auto mb-4 w-32 object-contain"
          />
        </div>

        <div className="bg-gray-900 border border-gray-700 rounded-lg p-6">
          <div className="text-center mb-6">
            <AlertCircle className={`w-16 h-16 mx-auto mb-4 ${isBanned ? 'text-red-500' : 'text-yellow-500'}`} />
            <h2 className="text-xl font-bold text-white mb-2">
              Account {isBanned ? 'Banned' : 'Suspended'}
            </h2>
            <p className="text-gray-400">
              {isBanned
                ? 'Your account has been permanently banned.'
                : `Your account has been suspended${currentUser.suspensionEndDate && currentUser.suspensionEndDate !== 'permanent' ? ` until ${new Date(currentUser.suspensionEndDate).toLocaleDateString()}` : ' permanently'}.`}
            </p>
          </div>

          <div className={`${isBanned ? 'bg-red-900/50 border-red-700' : 'bg-yellow-900/50 border-yellow-700'} border rounded-lg p-4 mb-6`}>
            <p className="text-sm font-bold text-white mb-1">Reason:</p>
            <p className="text-sm text-gray-300">{penaltyReason}</p>
          </div>

          {!showDisputeForm ? (
            <div className="space-y-3">
              <button
                onClick={() => setShowDisputeForm(true)}
                disabled={hasPendingDispute}
                className={`w-full py-2 rounded-lg font-medium ${
                  hasPendingDispute
                    ? 'bg-gray-700 text-gray-300 cursor-not-allowed'
                    : 'bg-[#16a34a] text-pure-white hover:bg-[#15803d]'
                }`}
              >
                {hasPendingDispute ? 'Dispute Pending Review' : 'Open Dispute'}
              </button>
              <button
                onClick={async () => { try { await logoutUser(); } catch(e) {} }}
                className="w-full py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700"
              >
                Log Out
              </button>
            </div>
          ) : (
            <div>
              <h3 className="text-sm font-bold text-white mb-2">Submit a Dispute</h3>
              <p className="text-xs text-gray-400 mb-3">Explain why you believe this action should be reversed.</p>
              <textarea
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                placeholder="Explain your case..."
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm resize-none h-28 mb-3"
                maxLength={1000}
              />
              <div className="flex space-x-2">
                <button
                  onClick={handleSubmitDispute}
                  disabled={!disputeReason.trim() || submitting}
                  className="flex-1 py-2 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d] disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {submitting ? 'Submitting...' : 'Submit Dispute'}
                </button>
                <button
                  onClick={() => { setShowDisputeForm(false); setDisputeReason(''); }}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Previous Disputes */}
        {!loadingDisputes && myDisputes.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-bold text-gray-400 mb-3">Previous Disputes</h3>
            <div className="space-y-3">
              {myDisputes.map(dispute => (
                <div key={dispute.id} className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      dispute.status === 'pending' ? 'bg-yellow-800 text-pure-white' :
                      dispute.status === 'approved' ? 'bg-green-800 text-pure-white' :
                      'bg-red-800 text-pure-white'
                    }`}>
                      {dispute.status}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(dispute.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-300">{dispute.reason}</p>
                  {dispute.resolution && (
                    <div className="mt-2 pt-2 border-t border-gray-700">
                      <p className="text-xs text-gray-400">Response: {dispute.resolution}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RestrictedScreen;
